// src/components/Fabricantes/FabricanteIndex.tsx

import { useState, useEffect, useRef } from "react";
import * as S from "./Valvula.styles";

// Importaciones de DevExtreme
import DataGrid, {
  Column,
  HeaderFilter,
  Paging,
  Toolbar,
  Item,
  SearchPanel,
  FilterRow,
} from "devextreme-react/data-grid";
import { Popup } from "devextreme-react/popup";
import Form, { SimpleItem, Label, RequiredRule } from "devextreme-react/form";
import type { default as FormInstance } from "devextreme/ui/form";
import { Button } from "devextreme-react/button";
import { FiEdit } from "react-icons/fi";
import notify from "devextreme/ui/notify";

import axios, { AxiosError } from "axios";
import { ApiError } from "../../../services/apiError";
import { apiService } from "../../../services/apiService";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver";
import { exportDataGrid } from "devextreme/excel_exporter";
import { Valvula } from "../../../dtos/Valvula";
import { useNavigate } from "react-router-dom";

const handleApiError = (err: unknown): ApiError => {
  if (axios.isAxiosError(err)) {
    const error = err as AxiosError<ApiError>;
    const errorMessage =
      error.response?.data?.message || "Ocurrió un error en el servidor.";
    return {
      message: errorMessage,
      status: error.response?.status,
      details: error.response?.data?.details || null,
    };
  }
  return {
    message: "Ocurrió un error inesperado. Revisa tu conexión de red.",
  };
};

const ValvulaIndex = () => {
  const [dataSource, setDataSource] = useState<Valvula[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFormPopupVisible, setIsFormPopupVisible] = useState(false);
  const [editingItem, setEditingItem] = useState<Partial<Valvula> | null>(null);
  const dataGridRef = useRef<any>(null);
  const navigate = useNavigate();
  const formRef = useRef<FormInstance | null>(null);
  const handleExportExcel = async () => {
    const gridInstance = dataGridRef.current?.instance();

    if (!gridInstance) {
      notify("No se pudo obtener la tabla para exportar.", "error", 3000);
      return;
    }

    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet("Válvulas");

    await exportDataGrid({
      component: gridInstance,
      worksheet,
      autoFilterEnabled: true,

      customizeCell: ({ gridCell, excelCell }) => {
        if (gridCell && gridCell.rowType === "header") {
          excelCell.font = { bold: true };
        }
      },
    });

    const buffer = await workbook.xlsx.writeBuffer();

    saveAs(
      new Blob([buffer], { type: "application/octet-stream" }),
      "Valvulas.xlsx",
    );
  };
  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await apiService.get<Valvula[]>(
        "/api/Catalogos/Valvulas",
      );
      setDataSource(response);
    } catch (err: any) {
      notify(handleApiError(err).message, "error", 3000);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleAdd = () => {
    setEditingItem({ estatusActivo: false }); // Un nuevo item no está borrado (está activo)
    setIsFormPopupVisible(true);
  };

  const handleEdit = (data: Valvula) => {
    setEditingItem({ ...data });
    setIsFormPopupVisible(true);
  };

  /*const handleDelete = (data: Valvula) => {
        setItemToDelete(data);
        setIsDeletePopupVisible(true);
    };*/

  /*const confirmDelete = async () => {
        if (itemToDelete) {
            try {
                // La API invierte el estado actual, por lo que no se envía un cuerpo
                await apiService.patch(`/api/Catalogos/Fabricantes/${itemToDelete.id}/cambiastatus`, '');
                fetchFabric();
                notify('Cambio de estatus realizado con éxito.', 'success', 2000);
            } catch (error) {
                notify('Error al cambiar el estatus del registro.', 'error', 3000);
            } finally {
                setIsDeletePopupVisible(false);
                setItemToDelete(null);
            }
        }
    };*/

  const handleSave = async () => {
    const form = formRef.current;
    if (!form) return;

    const validation = await form.validate();
    if (!validation.isValid) {
      notify(
        "Por favor, complete todos los campos requeridos.",
        "warning",
        2000,
      );
      return;
    }

    const formData = form.option("formData") as Valvula;

    try {
      if (formData.idValvula) {
        // Actualizar (PUT)
        const payload = { ...formData };
        const response = await apiService.put<Valvula, Valvula>(
          `/api/Catalogos/Valvulas/${formData.idValvula}`,
          payload,
        );
        setDataSource((prev) =>
          prev.map((item) =>
            item.idValvula === formData.idValvula ? response : item,
          ),
        );
        notify("Fabricante actualizado con éxito.", "success", 2000);
      } else {
        // Crear (POST)
        const response = await apiService.post<Valvula, Valvula>(
          "/api/Catalogos/Valvulas",
          formData,
        );
        setDataSource((prev) => [...prev, response]);
        notify("Fabricante creado con éxito.", "success", 2000);
      }
      setIsFormPopupVisible(false);
    } catch (error) {
      notify("Error al guardar el registro.", "error", 3000);
    }
  };

  const EstatusCell = (cellInfo: any) => {
    const isDeleted = cellInfo.value; // El campo se llama isDeleted
    const isActive = !isDeleted;
    return (
      <div
        style={{
          padding: "4px 10px",
          borderRadius: "12px",
          fontSize: "12px",
          fontWeight: 500,
          color: "#fff",
          backgroundColor: isActive ? "#16a34a" : "#71717a",
          textAlign: "center",
        }}
      >
        {isActive ? "Activo" : "Inactivo"}
      </div>
    );
  };

  const ActionsCell = (cellData: any) => {
    const item = cellData.data as Valvula;
    //const isActive = !item.estatusActivo;
    return (
      <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
        <Button
          hint="Editar"
          onClick={() => handleEdit(item)}
          stylingMode="text"
        >
          <FiEdit size={18} color="#1d4ed8" />
        </Button>
        {/*<Button
                    hint={isActive ? "Desactivar" : "Activar"}
                    onClick={() => handleDelete(item)}
                    stylingMode="text"
                >
                    {isActive ? (
                        <FiTrash2 size={18} color="#dc2626" />
                    ) : (
                        <FiCheckCircle size={18} color="#16a34a" />
                    )}
                </Button>*/}
      </div>
    );
  };

  return (
    <S.PageWrapper>
      {/* CAMBIO 1: Activa los estilos globales para el título */}
      <S.GlobalPopupStyles />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "15px",
        }}
      >
        <Button
          icon="chevronleft"
          text="Regresar"
          stylingMode="outlined"
          type="default"
          onClick={() => navigate(-1)} // ← vuelve a la pestaña anterior
        />
      </div>
      <S.Header>Catálogo de válvulas</S.Header>
      <DataGrid
        ref={dataGridRef}
        dataSource={dataSource}
        keyExpr="idValvula" // <-- CORRECCIÓN: La clave principal es 'id'
        showBorders={true}
        columnAutoWidth={true}
        hoverStateEnabled={true}
        loadPanel={{ enabled: loading }}
      >
        <FilterRow visible={true} />
        <HeaderFilter visible={true} allowSearch={true} />
        <Paging defaultPageSize={50} />
        <Toolbar>
          <Item name="searchPanel" />
          <Item
            location="after"
            widget="dxButton"
            options={{
              text: "Exportar Excel",
              icon: "xlsxfile",
              stylingMode: "contained",
              type: "default",
              onClick: handleExportExcel,
            }}
          />
          <Item
            location="after"
            widget="dxButton"
            options={{
              text: "Añadir valvula",
              icon: "add",
              stylingMode: "contained",
              type: "default",
              onClick: handleAdd,
            }}
          />
        </Toolbar>
        <SearchPanel
          visible={true}
          highlightCaseSensitive={true}
          placeholder="Buscar..."
        />
        <Column dataField="descripcion" caption="Descripción" width={300} />
        <Column
          dataField="estatusActivo"
          caption="Estatus"
          alignment="center"
          cellRender={EstatusCell}
          visible={false}
        />
        <Column
          caption="Acciones"
          alignment="center"
          width={300}
          cellRender={ActionsCell}
          allowExporting={false}
        />
      </DataGrid>

      {/* --- Popup de Edición / Creación MODIFICADO --- */}
      <Popup
        visible={isFormPopupVisible}
        onHiding={() => setIsFormPopupVisible(false)}
        dragEnabled={false}
        showCloseButton={true}
        showTitle={true}
        title={
          editingItem?.idValvula
            ? `Editar Fabricante: ${editingItem.descripcion}`
            : "Añadir nueva valvula"
        }
        width={500}
        height="auto"
        className="custom-form-popup" // <-- Clase para que funcionen los estilos
        // CAMBIO 2: Se elimina por completo la propiedad 'toolbarItems'
      >
        <S.FormContainer>
          <Form
            formData={editingItem}
            onInitialized={(e) => {
              formRef.current = e.component ?? null;
            }}
          >
            <SimpleItem dataField="descripcion">
              <Label text="Descripción" />
              <RequiredRule message="La descripcion es requerido." />
            </SimpleItem>
          </Form>
        </S.FormContainer>

        {/* CAMBIO 3: Se añade el footer personalizado con nuestros botones */}
        <S.PopupFooter>
          <S.CancelButton onClick={() => setIsFormPopupVisible(false)}>
            Cancelar
          </S.CancelButton>
          <S.SaveButton onClick={handleSave}>Guardar</S.SaveButton>
        </S.PopupFooter>
      </Popup>

      {/* El popup de confirmación se queda igual, solo ajustamos la lógica del texto */}
      {/*<Popup
                visible={isDeletePopupVisible}
                onHiding={() => setIsDeletePopupVisible(false)}
                dragEnabled={false}
                showTitle={true}
                title={`Confirmar ${!itemToDelete?.isDeleted ? 'Desactivación' : 'Activación'}`}
                width={400}
                height="auto"
                toolbarItems={[
                    {
                        toolbar: 'bottom', location: 'after', widget: 'dxButton',
                        options: {
                            text: `Sí, ${!itemToDelete?.isDeleted ? 'Desactivar' : 'Activar'}`,
                            type: !itemToDelete?.isDeleted ? 'danger' : 'success',
                            stylingMode: 'contained',
                            onClick: confirmDelete
                        }
                    },
                    {
                        toolbar: 'bottom', location: 'after', widget: 'dxButton',
                        options: { text: 'Cancelar', stylingMode: 'text', onClick: () => setIsDeletePopupVisible(false) }
                    }
                ]}
            >
                <div style={{ padding: '20px', textAlign: 'center' }}>
                    <p>¿Estás seguro de que deseas <strong>{!itemToDelete?.isDeleted ? 'desactivar' : 'activar'}</strong> el fabricante</p>
                    <p style={{ fontWeight: 'bold', margin: '10px 0' }}>"{itemToDelete?.nombre}"?</p>
                </div>
            </Popup>*/}
    </S.PageWrapper>
  );
};

export default ValvulaIndex;
